import axios from "axios";

// Validation functions
export const validateFormData = (formData, currentConfig) => {
  if (!formData.propertyType || !currentConfig) {
    return { isValid: false, errors: ["Vui lòng chọn loại bất động sản"] };
  }

  const errors = [];

  // Check required dropdown fields
  for (const field of currentConfig.dropdownFields) {
    if (field.required && !formData[field.key]) {
      // Check conditional fields
      if (field.conditional && !field.conditional(formData)) {
        continue;
      }
      errors.push(`Vui lòng chọn ${field.label.toLowerCase()}`);
    }
  }

  // Check required input fields
  for (const field of currentConfig.inputFields) {
    if (field.required && !formData[field.key]) {
      errors.push(`Vui lòng nhập ${field.label.toLowerCase()}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Format number with Vietnamese currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format area
export const formatArea = (area) => {
  return `${area}m²`;
};

// Save form data to localStorage
export const saveFormDataToStorage = (formData, prefix = "aiPrice") => {
  try {
    localStorage.setItem(`${prefix}_formData`, JSON.stringify(formData));
  } catch (error) {
    console.error("Error saving form data to localStorage:", error);
  }
};

// Load form data from localStorage
export const loadFormDataFromStorage = (prefix = "aiPrice") => {
  try {
    const savedData = localStorage.getItem(`${prefix}_formData`);
    return savedData ? JSON.parse(savedData) : null;
  } catch (error) {
    console.error("Error loading form data from localStorage:", error);
    return null;
  }
};

// Clear form data from localStorage
export const clearFormDataFromStorage = (prefix = "aiPrice") => {
  try {
    localStorage.removeItem(`${prefix}_formData`);
  } catch (error) {
    console.error("Error clearing form data from localStorage:", error);
  }
};

// Generate price estimation based on form data
export const generatePriceEstimation = (formData) => {
  // This would typically call a backend API
  // For now, we'll generate mock data based on property type

  // Debug logging to understand property types
  console.log('🔍 [generatePriceEstimation] Property type:', formData.propertyType);
  console.log('🔍 [generatePriceEstimation] Form data:', formData);

  const mockPrices = {
    apartment: {
      min: 2000000000, // 2 tỷ
      max: 4000000000, // 4 tỷ
      pricePerM2Min: 40000000, // 40 triệu/m²
      pricePerM2Max: 70000000, // 70 triệu/m²
    },
    low_rise_project: {
      min: 5000000000, // 5 tỷ
      max: 15000000000, // 15 tỷ
      pricePerM2Min: 25000000, // 25 triệu/m²
      pricePerM2Max: 45000000, // 45 triệu/m²
    },
    land_project: {
      min: 3000000000, // 3 tỷ
      max: 10000000000, // 10 tỷ
      pricePerM2Min: 15000000, // 15 triệu/m²
      pricePerM2Max: 35000000, // 35 triệu/m²
    },
    private_house: {
      min: 4000000000, // 4 tỷ
      max: 12000000000, // 12 tỷ
      pricePerM2Min: 30000000, // 30 triệu/m²
      pricePerM2Max: 60000000, // 60 triệu/m²
    },
    other_property: {
      min: 2000000000, // 2 tỷ
      max: 20000000000, // 20 tỷ
      pricePerM2Min: 10000000, // 10 triệu/m²
      pricePerM2Max: 50000000, // 50 triệu/m²
    },
  };

  // Better fallback logic: if property type is not recognized, try to infer from form data
  let priceData = mockPrices[formData.propertyType];

  if (!priceData) {
    console.warn('⚠️ [generatePriceEstimation] Unrecognized property type:', formData.propertyType);

    // Try to infer property type from form data
    if (formData.landArea && !formData.area) {
      // Likely land or house with land
      if (formData.houseType && formData.houseType.includes('liên kế')) {
        priceData = mockPrices.low_rise_project;
        console.log('🔄 [generatePriceEstimation] Inferred as low_rise_project');
      } else if (formData.landType) {
        priceData = mockPrices.land_project;
        console.log('🔄 [generatePriceEstimation] Inferred as land_project');
      } else {
        priceData = mockPrices.private_house;
        console.log('🔄 [generatePriceEstimation] Inferred as private_house');
      }
    } else {
      // Default to apartment for urban properties
      priceData = mockPrices.apartment;
      console.log('🔄 [generatePriceEstimation] Defaulted to apartment');
    }
  }

  // Add some variation based on specific inputs
  let multiplier = 1;

  // Adjust based on area (if available)
  const area = parseFloat(formData.area || formData.landArea || 100);
  if (area > 200) multiplier *= 1.3;
  else if (area > 150) multiplier *= 1.1;
  else if (area < 50) multiplier *= 0.7;
  else if (area < 70) multiplier *= 0.85;

  // Adjust based on location (mock logic)
  const address = formData.address || "";
  const addressLower = address.toLowerCase();

  if (addressLower.includes("quận 1") || addressLower.includes("district 1")) {
    multiplier *= 1.8;
  } else if (addressLower.includes("quận 2") || addressLower.includes("district 2")) {
    multiplier *= 1.6;
  } else if (addressLower.includes("quận 3") || addressLower.includes("district 3")) {
    multiplier *= 1.5;
  } else if (addressLower.includes("quận 7") || addressLower.includes("district 7") ||
             addressLower.includes("quận 4") || addressLower.includes("district 4")) {
    multiplier *= 1.3;
  } else if (addressLower.includes("bình thạnh") || addressLower.includes("thủ đức")) {
    multiplier *= 1.4;
  } else if (addressLower.includes("quận 8") || addressLower.includes("quận 9") ||
             addressLower.includes("quận 10") || addressLower.includes("quận 11") ||
             addressLower.includes("quận 12")) {
    multiplier *= 0.9;
  }

  // Adjust based on direction
  if (
    formData.balconyDirection === "Nam" ||
    formData.doorDirection === "Nam" ||
    formData.landDirection === "Nam"
  ) {
    multiplier *= 1.15;
  } else if (
    formData.balconyDirection === "Bắc" ||
    formData.doorDirection === "Bắc" ||
    formData.landDirection === "Bắc"
  ) {
    multiplier *= 0.9;
  }

  // Adjust based on legal status
  if (formData.legalStatus === "Sổ hồng/Sổ đỏ") {
    multiplier *= 1.1;
  } else if (formData.legalStatus === "Hợp đồng mua bán") {
    multiplier *= 0.85;
  }

  // Adjust based on construction year
  const constructionYear = parseInt(formData.constructionYear);
  if (constructionYear && constructionYear > 2020) {
    multiplier *= 1.05;
  } else if (constructionYear && constructionYear < 2010) {
    multiplier *= 0.9;
  }

  // Add some randomness to avoid identical results
  const randomFactor = 0.9 + (Math.random() * 0.2); // 0.9 to 1.1
  multiplier *= randomFactor;

  const estimatedMin = Math.round(priceData.min * multiplier);
  const estimatedMax = Math.round(priceData.max * multiplier);
  const pricePerM2Min = Math.round(priceData.pricePerM2Min * multiplier);
  const pricePerM2Max = Math.round(priceData.pricePerM2Max * multiplier);

  console.log('💰 [generatePriceEstimation] Final multiplier:', multiplier.toFixed(2));
  console.log('💰 [generatePriceEstimation] Final prices:', {
    estimatedMin: formatCurrency(estimatedMin),
    estimatedMax: formatCurrency(estimatedMax),
    pricePerM2Min: formatCurrency(pricePerM2Min),
    pricePerM2Max: formatCurrency(pricePerM2Max)
  });

  return {
    estimatedPrice: `${formatCurrency(estimatedMin)} - ${formatCurrency(
      estimatedMax
    )}`,
    pricePerM2: `${formatCurrency(pricePerM2Min)} - ${formatCurrency(
      pricePerM2Max
    )}/m²`,
    marketTrend: getRandomMarketTrend(),
    confidence: getRandomConfidence(),
    similarProperties: generateSimilarProperties(formData),
    marketInsights: generateMarketInsights(formData),
  };
};

// Helper function to get random market trend
const getRandomMarketTrend = () => {
  const trends = ["Tăng mạnh", "Tăng nhẹ", "Ổn định", "Giảm nhẹ", "Biến động"];
  return trends[Math.floor(Math.random() * trends.length)];
};

// Helper function to get random confidence level
const getRandomConfidence = () => {
  const confidences = ["75%", "80%", "85%", "90%", "95%"];
  return confidences[Math.floor(Math.random() * confidences.length)];
};

// Generate similar properties data
const generateSimilarProperties = (formData) => {
  const mockProperties = [
    {
      address: "Căn hộ tầng 15, Vinhomes Central Park, Quận Bình Thạnh",
      price: "3.2 tỷ",
      area: "75m²",
    },
    {
      address: "Căn hộ tầng 22, Landmark 81, Quận Bình Thạnh",
      price: "4.5 tỷ",
      area: "80m²",
    },
    {
      address: "Căn hộ tầng 10, The Manor, Quận Bình Thạnh",
      price: "2.8 tỷ",
      area: "70m²",
    },
  ];

  // Return a subset based on property type
  return mockProperties.slice(0, Math.floor(Math.random() * 3) + 1);
};

// Generate market insights
const generateMarketInsights = (formData) => {
  const baseInsights = [
    "Thị trường BĐS khu vực đang có xu hướng phục hồi tích cực",
    "Giá BĐS dự kiến sẽ tăng 3-5% trong 6 tháng tới",
    "Đây là thời điểm thuận lợi để đầu tư hoặc bán BĐS",
  ];

  const propertySpecificInsights = {
    apartment: [
      "Căn hộ có hướng Nam được định giá cao hơn 5-10%",
      "Căn hộ tại các dự án có tiện ích đầy đủ được ưa chuộng",
      "Thị trường căn hộ trung cấp đang có nhu cầu cao",
    ],
    low_rise_project: [
      "Biệt thự và nhà liền kề đang có xu hướng tăng giá mạnh",
      "Các dự án có không gian xanh được đánh giá cao",
      "Nhà thấp tầng gần trung tâm có giá trị đầu tư tốt",
    ],
    land_project: [
      "Đất nền dự án có pháp lý rõ ràng được ưu tiên",
      "Đất có mặt tiền rộng có giá trị cao hơn đáng kể",
      "Khu vực có quy hoạch phát triển có tiềm năng tăng giá",
    ],
    private_house: [
      "Nhà mặt phố kinh doanh được định giá cao hơn nhà riêng",
      "Nhà có thiết kế hiện đại được ưa chuộng",
      "Vị trí gần trường học, bệnh viện là yếu tố cộng điểm",
    ],
    other_property: [
      "BĐS thương mại có tiềm năng sinh lời cao",
      "Kho xưởng gần cảng, sân bay có giá trị tốt",
      "Đất nông nghiệp chuyển đổi mục đích sử dụng có triển vọng",
    ],
  };

  const specific = propertySpecificInsights[formData.propertyType] || [];
  return [...baseInsights, ...specific].slice(0, 4);
};

// API call for real price analysis (placeholder)
export const callPriceAnalysisAPI = async (formData) => {
  try {
    // This would be a real API call
    const response = await axios.post("/api/ai-price/analyze", {
      propertyData: formData,
      userId: localStorage.getItem("userId"),
    });

    return response.data;
  } catch (error) {
    console.error("Error calling price analysis API:", error);
    throw error;
  }
};

// Export data for debugging or reports
export const exportFormData = (formData, priceResult) => {
  const exportData = {
    timestamp: new Date().toISOString(),
    formData,
    priceResult,
    userId: localStorage.getItem("userId"),
  };

  const dataStr = JSON.stringify(exportData, null, 2);
  const dataBlob = new Blob([dataStr], { type: "application/json" });

  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ai-price-analysis-${Date.now()}.json`;
  link.click();

  URL.revokeObjectURL(url);
};
